import { useState, useEffect, useCallback } from "react";
import { Control, FieldValues, Path, PathValue, UseFormSetValue, useWatch } from "react-hook-form";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { ControlledCheckbox } from "@/components/ui/customs/controlled-checkbox";
import { ControlledCountryComboboxCountryCode } from "@/components/ui/customs/controlled-combobox-countryCode";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useLookupSiret } from "@/api/insee.api";
import { Search, Loader2, Trash2, TriangleAlert, LinkIcon } from "lucide-react";
import { toast } from "react-toastify";
import { Trans, useTranslation } from "react-i18next";

type DomiciliationType = "france" | "other";

interface BillingEntityFormProps<T extends FieldValues> {
  control: Control<T>;
  prefix?: string;
  showIsDefault?: boolean;
  isDefaultDisabled?: boolean;
  isEditing?: boolean;
  setValue?: UseFormSetValue<T>;
  onCanSubmitChange?: (canSubmit: boolean) => void;
}

export const BillingEntityForm = <T extends FieldValues>({
  control,
  prefix = "",
  showIsDefault = false,
  isDefaultDisabled = false,
  isEditing = false,
  setValue,
  onCanSubmitChange,
}: BillingEntityFormProps<T>) => {
  const { t } = useTranslation("shared");
  const [disabledFields, setDisabledFields] = useState<string[]>([]);
  const [domiciliation, setDomiciliation] = useState<DomiciliationType>("france");
  const fieldName = useCallback(
    (name: string) => (prefix ? `${prefix}.${name}` : name) as Path<T>,
    [prefix],
  );

  const companySiret = useWatch({
    control,
    name: fieldName("companySiret"),
  });

  const noEori = useWatch({
    control,
    name: fieldName("noEori"),
  });

  const companySiretValue = (companySiret as string) || "";
  const noEoriValue = (noEori as boolean) || false;

  const lookupSiretMutation = useLookupSiret();

  const handleSiretLookup = async () => {
    if (!companySiretValue || companySiretValue.trim().length === 0) {
      toast.warning(t("billingEntityForm.siretMissing"));
      return;
    }

    const result = await lookupSiretMutation.mutateAsync(companySiretValue);

    if (result && setValue) {
      if (!result.isActive) {
        toast.warning(t("billingEntityForm.siretInactive"));
      }
      const setField = (name: string, value: string) =>
        setValue(fieldName(name), value as PathValue<T, Path<T>>);

      const fieldsToDisable: string[] = ["companySiret"];

      if (result.companyName) {
        setField("companyName", result.companyName);
        fieldsToDisable.push("companyName");
      }
      if (result.street) {
        setField("street", result.street);
        fieldsToDisable.push("street");
      }
      if (result.postalCode) {
        setField("postalCode", result.postalCode);
        fieldsToDisable.push("postalCode");
      }
      if (result.city) {
        setField("city", result.city);
        fieldsToDisable.push("city");
      }
      if (result.numberTVA) {
        setField("numberTVA", result.numberTVA);
        fieldsToDisable.push("numberTVA");
      }
      if (result.companyEori) {
        setField("companyEori", result.companyEori);
        fieldsToDisable.push("companyEori");
      }

      setDisabledFields(fieldsToDisable);
      toast.success(t("billingEntityForm.siretLookupSuccess"));
    }
  };

  const handleClearForm = () => {
    if (!setValue) return;
    const setField = <V,>(name: string, value: V) =>
      setValue(fieldName(name), value as PathValue<T, Path<T>>);
    setField("companySiret", "");
    setField("companyName", "");
    setField("street", "");
    setField("postalCode", "");
    setField("city", "");
    setField("country", "");
    setField("numberTVA", "");
    setField("companyEori", "");
    setField("noEori", false);
    setDisabledFields([]);
  };

  const isFrance = domiciliation === "france";
  const showAllFields = !isFrance || disabledFields.length > 0;
  const isFieldDisabled = (name: string) => disabledFields.includes(name);

  // Set country to "FR" when domiciliation is "france" (only in creation mode)
  useEffect(() => {
    if (!isEditing && isFrance && setValue) {
      setValue(fieldName("country"), "FR" as PathValue<T, Path<T>>);
    }
  }, [isEditing, isFrance, setValue, fieldName]);

  useEffect(() => {
    onCanSubmitChange?.(isEditing || showAllFields);
  }, [isEditing, showAllFields, onCanSubmitChange]);

  return (
    <>
      {!isEditing && (
        <div className="mb-4">
          <Label className="mb-2 block text-sm font-medium">
            {t("billingEntityForm.domiciliationLabel")}
          </Label>
          <RadioGroup
            value={domiciliation}
            onValueChange={(value) => {
              setDomiciliation(value as DomiciliationType);
              handleClearForm();
            }}
            className="flex flex-row gap-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="france" id={`${prefix}-domiciliation-france`} />
              <Label htmlFor={`${prefix}-domiciliation-france`} className="cursor-pointer">
                {t("billingEntityForm.domiciliationFrance")}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="other" id={`${prefix}-domiciliation-other`} />
              <Label htmlFor={`${prefix}-domiciliation-other`} className="cursor-pointer">
                {t("billingEntityForm.domiciliationOther")}
              </Label>
            </div>
          </RadioGroup>
        </div>
      )}

      {isFrance && !isEditing && (
        <div className="flex flex-row items-end gap-2">
          <ControlledUnifiedInput
            control={control}
            name={fieldName("companySiret")}
            label={t("billingEntityForm.siretLabel")}
            wrapperClassName="flex-1"
            disabled={isFieldDisabled("companySiret")}
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleSiretLookup}
            disabled={lookupSiretMutation.isPending || isFieldDisabled("companySiret")}
            className="mb-0.5 h-10 w-10 shrink-0"
            title={t("billingEntityForm.siretLookup")}
          >
            {lookupSiretMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
          </Button>
          {showAllFields && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handleClearForm}
              className="mb-0.5 h-10 w-10 shrink-0"
              title={t("billingEntityForm.clearForm")}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}

      {(showAllFields || isEditing) && (
        <>
          <ControlledUnifiedInput
            control={control}
            name={fieldName("companyName")}
            label={t("billingEntityForm.companyNameLabel")}
            disabled={isFieldDisabled("companyName")}
          />
          <ControlledUnifiedInput
            control={control}
            name={fieldName("street")}
            label={t("billingEntityForm.streetLabel")}
            disabled={isFieldDisabled("street")}
          />
          <div className="flex flex-row gap-2">
            <ControlledUnifiedInput
              control={control}
              name={fieldName("postalCode")}
              label={t("billingEntityForm.postalCodeLabel")}
              wrapperClassName="w-[30%]"
              disabled={isFieldDisabled("postalCode")}
            />
            <ControlledUnifiedInput
              control={control}
              name={fieldName("city")}
              label={t("billingEntityForm.cityLabel")}
              wrapperClassName="w-[70%]"
              disabled={isFieldDisabled("city")}
            />
          </div>
          {!isFrance && (
            <ControlledCountryComboboxCountryCode
              control={control}
              name={fieldName("country")}
              label={t("billingEntityForm.countryLabel")}
            />
          )}
          <ControlledUnifiedInput
            control={control}
            name={fieldName("numberTVA")}
            label={t("billingEntityForm.vatLabel")}
            disabled={isFieldDisabled("numberTVA")}
          />
          {!isFrance && (
            <ControlledUnifiedInput
              control={control}
              name={fieldName("companySiret")}
              label={t("billingEntityForm.siretLabel")}
            />
          )}
          {!noEoriValue && (
            <ControlledUnifiedInput
              control={control}
              name={fieldName("companyEori")}
              label={t("billingEntityForm.eoriLabel")}
              tooltip={t("billingEntityForm.eoriHelp")}
              disabled={isFieldDisabled("companyEori")}
            />
          )}
          {!isFieldDisabled("companyEori") && (
            <ControlledCheckbox
              control={control}
              name={fieldName("noEori")}
              label={t("billingEntityForm.noEoriLabel")}
              onCheckedChange={(checked: boolean) => {
                if (checked && setValue) {
                  setValue(fieldName("companyEori"), "" as PathValue<T, Path<T>>);
                }
              }}
            />
          )}
          {noEoriValue && (
            <Alert className="border-yellow-500 bg-yellow-50">
              <TriangleAlert className="h-4 w-4 -translate-y-1" color="#F59E0B" />
              <AlertDescription className="text-yellow-800">
                <Trans
                  t={t}
                  i18nKey="billingEntityForm.noEoriWarning"
                  components={{
                    link: (
                      <a
                        href="https://www.douane.gouv.fr/service-en-ligne/demande-dautorisation-douaniere-et-fiscale-soprano-acces-simplifie"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-yellow-900"
                      />
                    ),
                    icon: <LinkIcon className="ml-1 inline h-3 w-3" />,
                  }}
                />
              </AlertDescription>
            </Alert>
          )}
          <ControlledUnifiedInput
            control={control}
            name={fieldName("email")}
            label={t("billingEntityForm.emailLabel")}
          />
          <ControlledUnifiedInput
            control={control}
            name={fieldName("emailBis")}
            label={t("billingEntityForm.emailBisLabel")}
          />
          {showIsDefault && (
            <ControlledCheckbox
              control={control}
              name={fieldName("isDefault")}
              label={t("billingEntityForm.isDefaultLabel")}
              disabled={isDefaultDisabled}
            />
          )}
        </>
      )}
    </>
  );
};
